import { useState } from 'react';

import { useActiveWindowInterval } from '#/components/hooks';
import {
  getMarketSessionDisplayStatus,
  getSessionDisplayTickDelayMs,
  MarketSessionDisplayState,
  MarketSessionDisplayStatus,
  MarketSessionInfo,
} from '#/components/markets/intermittent/intermittentMarkets';

export type MarketSessionDisplay = {
  status: MarketSessionDisplayStatus;

  /** True while the engine restricts trading (closed and opening states) */
  isClosedForTrading: boolean;

  /** ≤60 min to the next transition (close while open, open while closed) — show mm:ss countdown */
  transitionInLessThan1Hour?: boolean;

  /** ≤5 min to the next transition — yellow warning color */
  transitionVeryClose?: boolean;

  /**
   * Timestamp of the next scheduled transition (close while open, open while
   * closed) — the countdown/time target for formatters like
   * {@link formatSessionDateOrCountdown}. Undefined while a transition is
   * pending or none is scheduled.
   */
  nextTransitionMs?: number;

  /**
   * Current time, ticking every second while a countdown is visible — pass it
   * to time formatters (e.g. {@link formatSessionDateOrCountdown}) at the
   * call site so countdowns stay live
   */
  nowMs: number;
};

/** {@link MarketSessionDisplay} without `nowMs` — the `on-state-change` mode result */
type MarketSessionState = Omit<MarketSessionDisplay, 'nowMs'>;

function isSameSessionDisplayState(
  a: MarketSessionDisplayState | null,
  b: MarketSessionDisplayState | null
): boolean {
  if (!a || !b) {
    return a === b;
  }
  return (
    a.state === b.state &&
    a.nextTransitionMs === b.nextTransitionMs &&
    !a.transitionInLessThan1Hour === !b.transitionInLessThan1Hour &&
    !a.transitionVeryClose === !b.transitionVeryClose
  );
}

/**
 * Derives the display session status of an intermittent market.
 * Returns null for continuous (crypto) markets — no interval is running for those.
 *
 * The `rerender` mode controls how often the caller re-renders:
 * - `every-tick` — re-renders on every tick (1 s while a countdown is
 *   visible, slower otherwise) and returns a live `nowMs` for countdown
 *   formatters. Use in small leaf components that render countdowns.
 * - `on-state-change` — re-renders only when the session state (or a
 *   proximity flag) actually changes; no `nowMs` is returned. Use in larger
 *   components (e.g. BuySellPanel) that only branch on the session state and
 *   would otherwise re-render every second.
 */
export function useIntermittentMarketSessionDisplay(
  info: MarketSessionInfo | undefined,
  rerender: 'every-tick'
): MarketSessionDisplay | null;
export function useIntermittentMarketSessionDisplay(
  info: MarketSessionInfo | undefined,
  rerender: 'on-state-change'
): MarketSessionState | null;
export function useIntermittentMarketSessionDisplay(
  info: MarketSessionInfo | undefined,
  rerender: 'every-tick' | 'on-state-change'
): MarketSessionDisplay | null {
  // In `on-state-change` mode nowMs advances only on state flips (stale in
  // between by design — the 60s cap in the tick delay bounds the staleness)
  const [nowMs, setNowMs] = useState(() => Date.now());

  const status = getMarketSessionDisplayStatus(info, nowMs);

  useActiveWindowInterval(
    () => {
      const nextNowMs = Date.now();
      if (rerender === 'on-state-change') {
        const nextStatus = getMarketSessionDisplayStatus(info, nextNowMs);
        if (isSameSessionDisplayState(status, nextStatus)) {
          return;
        }
      }
      setNowMs(nextNowMs);
    },
    // 1s only while a countdown is visible, slower/disabled otherwise
    getSessionDisplayTickDelayMs(info, nowMs)
  );

  const state = status?.state;
  if (!info || !state) {
    return null;
  }
  return {
    status: state,
    transitionInLessThan1Hour: status.transitionInLessThan1Hour,
    transitionVeryClose: status.transitionVeryClose,
    nextTransitionMs: status.nextTransitionMs,
    isClosedForTrading: state === 'closed' || state === 'opening',
    nowMs,
  };
}
